import { EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { InvoiceDateProcessingType } from '@app/core/models/invoices/invoice-date-processing-type.model';
import { InvoiceFilterConfig } from '@app/core/models/invoices/invoice-filter-config.model';
import { InvoiceFilters } from '@app/core/models/invoices/invoice-filters.model';
import { InvoiceSearchCriteriaModel } from '@app/core/models/invoices/invoice-search-criteria.model';
import { InvoiceModel } from '@app/core/models/invoices/invoice.model';
import { WaitingFlags } from '@app/core/models/referentials/waiting-flag.model';
import { SearchFilterModel } from '@app/core/models/search-filter.model';
import { ReferentialItem } from '@app/core/models/shared/referential-item.model';
import { AssetService } from '@app/core/services/asset.service';
import { CommonService } from '@app/core/services/common.service';
import { RecipientEntitiesService } from '@app/core/services/recipient-entities.service';
import { SessionStorageService } from '@app/core/services/session-storage.service';
import * as _ from 'lodash';
import * as moment from 'moment';

export class InvoiceFilterService extends CommonService {

  protected onAssetFetched(assets: ReferentialItem[]) {
    throw new Error('Method not implemented.');
  }
  protected onRecipientEntitiesFetched(recipientEntities: ReferentialItem[]) {
    throw new Error('Method not implemented.');
  }

  public onFiltersCleared: EventEmitter<void> = new EventEmitter<void>();
  public onExportExcelCaller: EventEmitter<void> = new EventEmitter<void>();

  public invoiceSearchCriteriaModel: InvoiceSearchCriteriaModel;
  protected filterStorageKey: string;
  protected invoiceFiltersConfig: InvoiceFilterConfig;

  constructor(
    protected route: ActivatedRoute,
    protected sessionStorageService: SessionStorageService,
    public assetService: AssetService,
    public recipientEntityService: RecipientEntitiesService) {
    super(assetService, recipientEntityService);
    this.filterStorageKey = this.route.snapshot.data.searchFilterKey;
    this.invoiceSearchCriteriaModel = new InvoiceSearchCriteriaModel();
  }

  dateChange(type: string, selectedDate: any): void {
    var formated = !_.isNil(selectedDate) ? moment(selectedDate).format('YYYY-MM-DD') : null;
    this.dateChangeString(type, formated);
  }

  dateChangeString(type: string, formated: string) {
    switch (type) {
      case 'FromReceptionDate':
        this.invoiceSearchCriteriaModel.FromReceptionDate = formated;
        break;
      case 'ToReceptionDate':
        this.invoiceSearchCriteriaModel.ToReceptionDate = formated;
        break;
      case 'FromProcessingDate':
        this.invoiceSearchCriteriaModel.FromProcessingDate = formated;
        break;
      case 'ToProcessingDate':
        this.invoiceSearchCriteriaModel.ToProcessingDate = formated;
        break;
      case 'FromInvoiceDate':
        this.invoiceSearchCriteriaModel.FromInvoiceDate = formated;
        break;
      case 'ToInvoiceDate':
        this.invoiceSearchCriteriaModel.ToInvoiceDate = formated;
        break;
      case 'FromDueDate':
        this.invoiceSearchCriteriaModel.FromDueDate = formated;
        break;
      case 'ToDueDate':
        this.invoiceSearchCriteriaModel.ToDueDate = formated;
        break;
    }
  }

  protected filterOnString(invoiceFieldValue: string, searchFilterValue: string): boolean {
    return _.isNil(searchFilterValue) || searchFilterValue === '' || invoiceFieldValue?.trim().toLowerCase().includes(searchFilterValue.trim().toLowerCase());
  }

  protected filterOnDate(invoiceFieldValue: any, startDate: Date | string, endDate: Date | string): boolean {
    if (!_.isNil(startDate) && !_.isNil(endDate)) {
      return moment(invoiceFieldValue).isBetween(startDate, endDate, 'day', '[]');
    }

    if (!_.isNil(startDate)) {
      return moment(invoiceFieldValue).isSameOrAfter(startDate, 'day');
    }

    if (!_.isNil(endDate)) {
      return moment(invoiceFieldValue).isSameOrBefore(endDate, 'day');
    }

    return true;
  }

  protected filterOnChoices(invoiceFieldValue: string, selectedItems: any[]): boolean {
    if (_.isNil(selectedItems) || _.isEmpty(selectedItems)) {
      return true;
    }

    if (_.isNil(invoiceFieldValue)) {
      return false;
    }

    return selectedItems.includes(invoiceFieldValue.trim().toLowerCase());
  }

  protected filterOnBoolean(invoiceFieldValue: boolean | '', value: boolean | ''): boolean {
    if (_.isNil(value) || value === '') {
      return true;
    }

    if (_.isNil(invoiceFieldValue) || invoiceFieldValue === '') {
      return false;
    }

    return invoiceFieldValue === value;
  }

  protected filterOnProcessingDateType(invoice: InvoiceModel, selectedProcessingDateType?: InvoiceDateProcessingType): Date | string {
    if (!selectedProcessingDateType || selectedProcessingDateType === InvoiceDateProcessingType.PROCESSEDAT) {
      return invoice.ProcessedAt;
    }

    if (selectedProcessingDateType === InvoiceDateProcessingType.WORKFLOWCOMPLETEDAT) {
      return invoice.WorkflowCompletionDate;
    }

    if (selectedProcessingDateType === InvoiceDateProcessingType.VOUCHERVALIDATEDAT) {
      return invoice.VoucherValidatedAt;
    }
  }

  protected storeFilters(extraFilters: any): void {
    var filter = new SearchFilterModel();
    filter.Filter = this.invoiceSearchCriteriaModel;
    filter.ExtraFilters = extraFilters;
    this.sessionStorageService.store(this.filterStorageKey, filter);
  }

  protected restoreFilters(restoreExtraFilters: (data: InvoiceFilters) => void): void {
    const searchFilter = this.sessionStorageService.get<SearchFilterModel<InvoiceSearchCriteriaModel>>(this.filterStorageKey);
    if (_.isNil(searchFilter)) {
      return;
    }
    
    this.invoiceSearchCriteriaModel = _.defaultTo(searchFilter.Filter, new InvoiceSearchCriteriaModel());
    if (!_.isNil(searchFilter.ExtraFilters) && !_.isNil(restoreExtraFilters)) {
      restoreExtraFilters(searchFilter.ExtraFilters);
    }
  }

  protected innerFilterInvoices(invoices: InvoiceModel[], invoiceFilters: InvoiceFilters, persistFilters: boolean): InvoiceModel[] {
    const selectedAssets = invoiceFilters.Assets?.map(a => a.Label.trim().toLowerCase());
    const selectedRecipientEntities = invoiceFilters.RecipientEntities?.map(a => a.Id.toString());
    const selectedRejections = invoiceFilters.RejectionTypes?.map(a => a.Id.toString());
    const selectedReverseFactoring = invoiceFilters.ReverseFactoring?.map(a => a.Id.toString());
    const selectedWorkingSituation = invoiceFilters.WorkingSituation?.map(a => a.Id.toString());
    const selectedPicked = invoiceFilters.Picked?.map(a => a.Id.toString());
    const selectedValidationTypes = invoiceFilters.ValidationTypes?.map(a => a.Id.toString());
    const selectedProcessingDateType = invoiceFilters.ProcessingDateType?.Id;
    const selectedWaitingFlags = invoiceFilters.WaitingFlags?.map(a => a.Id);
    const selectedComplaint = invoiceFilters.Complaint?.map(a => a.Id.toString());
    const selectedFamilyTypes = invoiceFilters.ExpenseFamilyTypes?.map(a => a.FamilyCode);
    const selectedTradeTypes = invoiceFilters.ExpenseTradeTypes?.map(a => a.Code);

    const filtredInvoices = _.filter(invoices, invoice => {
      return (!this.invoiceFiltersConfig.FilterOnNumber || this.filterOnString(invoice.Number, this.invoiceSearchCriteriaModel.Number))
        && (!this.invoiceFiltersConfig.FilterOnSupplier || this.filterOnString(invoice.SupplierComputedCompanyName, this.invoiceSearchCriteriaModel.SupplierName) || this.filterOnString(invoice.SupplierCompanyName, this.invoiceSearchCriteriaModel.SupplierName))
        && (!this.invoiceFiltersConfig.FilterOnInvoiceDate || this.filterOnDate(invoice.IssuedAt, this.invoiceSearchCriteriaModel.FromInvoiceDate, this.invoiceSearchCriteriaModel.ToInvoiceDate))
        && (!this.invoiceFiltersConfig.FilterOnReceptionDate || this.filterOnDate(invoice.CreationDate, this.invoiceSearchCriteriaModel.FromReceptionDate, this.invoiceSearchCriteriaModel.ToReceptionDate))
        && (!this.invoiceFiltersConfig.FilterOnProcessingDate || this.filterOnDate(this.filterOnProcessingDateType(invoice, selectedProcessingDateType), this.invoiceSearchCriteriaModel.FromProcessingDate, this.invoiceSearchCriteriaModel.ToProcessingDate))
        && (!this.invoiceFiltersConfig.FilterOnPrimaryReference || this.filterOnChoices(invoice.PrimaryReference, selectedAssets))
        && (!this.invoiceFiltersConfig.FilterOnRecipientEntity || this.filterOnChoices(invoice.RecipientEntityId?.toString(), selectedRecipientEntities))
        && (!this.invoiceFiltersConfig.FilterOnReverseFactoring || this.filterOnYesNo(invoice.IsReverseFactoring, selectedReverseFactoring))
        && (!this.invoiceFiltersConfig.FilterOnWorkingSituation || this.filterOnYesNo(invoice.IsWorkingSituation, selectedWorkingSituation))
        && (!this.invoiceFiltersConfig.FilterOnValidationTypes || this.filterOnYesNo(invoice.IsVoucher, selectedValidationTypes))
        && (!this.invoiceFiltersConfig.FilterOnControl || this.filterOnBoolean(invoice.Checked, invoiceFilters.Checked))
        && (!this.invoiceFiltersConfig.FilterOnPicked || this.filterOnYesNo(!_.isNil(invoice.PickedByUserId), selectedPicked))
        && (!this.invoiceFiltersConfig.FilterOnComplaint || this.filterOnYesNo(!_.isNil(invoice.ComplaintByUserId), selectedComplaint))
        && (!this.invoiceFiltersConfig.FilterOnRejectionType || this.filterOnChoices(invoice.RejectionFlagId.toString(), selectedRejections))
        && (!this.invoiceFiltersConfig.FilterOnSageErpCode || this.filterOnString(invoice.SupplierSageErpCode, this.invoiceSearchCriteriaModel.SupplierSageErpCode))
        && (!this.invoiceFiltersConfig.FilterOnDueDate || this.filterOnDate(invoice.DueDate, this.invoiceSearchCriteriaModel.FromDueDate, this.invoiceSearchCriteriaModel.ToDueDate))
        && (!this.invoiceFiltersConfig.FilterOnInvoiceWaitingFlags || this.filterOnWaitingFlags(invoice.WaitingFlagId, selectedWaitingFlags))

        && (!this.invoiceFiltersConfig.FilterOnExpenseFamilyTypes || this.filterOnChoices(invoice.FamilyCode, selectedFamilyTypes))
        && (!this.invoiceFiltersConfig.FilterOnExpenseTradeTypes || this.filterOnChoices(invoice.TradeCode, selectedTradeTypes))
    });

    if (persistFilters) {
      this.storeFilters(invoiceFilters);
    }

    return filtredInvoices;
  }

  private filterOnYesNo(invoiceFieldValue: boolean, selectedItems: any[]): boolean {
    if (_.isNil(selectedItems) || _.isEmpty(selectedItems)) {
      return true;
    }

    if (_.isNil(invoiceFieldValue)) {
      return false;
    }

    return selectedItems.includes(invoiceFieldValue ? "1" : "0")
  }

  private filterOnWaitingFlags(invoiceFieldValue: number, selectedItems: number[]): boolean {
    let result: boolean = selectedItems.length === 0;
    const invoiceWatingFlag: WaitingFlags = invoiceFieldValue;

    for (let index = 0; index < selectedItems.length; index++) {
      const selectedFlag:WaitingFlags = selectedItems[index];
      result = result || selectedFlag === (invoiceWatingFlag & selectedFlag);
    }

    return result;
  }
}
